
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Benchmark } from '../types/financial';
import {
  getMetricDefinitions,
  getAvailableYears,
  validateBenchmarkData,
  sortBenchmarksByYear,
  getBenchmarkForYear,
  hasMetricValue,
  isValidMetricNumber,
} from '../utils/financial';
import ErrorBoundary from './ErrorBoundary';
import LoadingSpinner from './LoadingSpinner';

interface YearComparisonTableProps {
  benchmarks: Benchmark[];
  isLoading?: boolean;
}

const YearComparisonTable: React.FC<YearComparisonTableProps> = ({ benchmarks, isLoading = false }) => {
  if (isLoading) {
    return <LoadingSpinner  />;
  }

  if (!benchmarks || benchmarks.length === 0) {
    return (
      <div className="text-center py-8 text-[#8b7355]/70">
        <p>No benchmark data available for comparison.</p>
      </div>
    );
  }

  const validBenchmarks = sortBenchmarksByYear(
    benchmarks.filter(validateBenchmarkData)
  );

  if (validBenchmarks.length === 0) {
    return (
      <div className="text-center py-8 text-[#8b7355]/70">
        <p>Invalid or incomplete benchmark data.</p>
      </div>
    );
  }

  const years = getAvailableYears(validBenchmarks);
  const metrics = getMetricDefinitions();

  return (
    <ErrorBoundary>
      <div className="overflow-x-auto rounded-lg border border-[#d9cba6]/30">
        <Table>
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-[#d9cba6]/10 to-[#d9cba6]/5 hover:bg-gradient-to-r hover:from-[#d9cba6]/15 hover:to-[#d9cba6]/8">
              <TableHead className="w-64 sticky left-0 bg-gradient-to-r from-[#d9cba6]/10 to-[#d9cba6]/5 z-10 border-r border-[#d9cba6]/30">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#8b7355]/70">📊</span>
                  <span className="font-semibold text-[#8b7355]">Metric</span>
                </div>
              </TableHead>
              {years.map((year) => (
                <TableHead key={year} className="text-center min-w-40 bg-gradient-to-b from-[#d9cba6]/20 to-[#d9cba6]/10">
                  <div className="font-bold text-[#8b7355] text-base">FY {year}</div>
                </TableHead>
              ))}
            </TableRow>
            <TableRow className="bg-[#d9cba6]/5 hover:bg-[#d9cba6]/10">
              <TableCell className="sticky left-0 bg-[#d9cba6]/5 z-10 border-r border-[#d9cba6]/30">
                <div className="text-xs text-[#8b7355]/80 font-medium">
                  # of Peers in Sample
                </div>
              </TableCell>
              {years.map((year) => {
                const benchmark = getBenchmarkForYear(validBenchmarks, year);
                return (
                  <TableCell key={year} className="text-center">
                    <div className="inline-block bg-[#d9cba6]/20 text-[#8b7355] font-semibold px-3 py-1 rounded-full text-sm">
                      {benchmark?.no_of_peers_in_sample ?? "—"}
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {metrics.map((metric, index) => (
              <TableRow key={metric.key} className={`hover:bg-[#d9cba6]/5 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-[#d9cba6]/3'}`}>
                <TableCell className="font-medium sticky left-0 bg-inherit z-10 border-r border-[#d9cba6]/20">
                  <div className="text-[#8b7355] font-semibold">{metric.label}</div>
                </TableCell>
                {years.map((year) => {
                  const benchmark = getBenchmarkForYear(validBenchmarks, year);
                  const hasData = hasMetricValue(
                    benchmark,
                    metric.key,
                    metric.medianKey
                  );
                  const companyValue = benchmark?.[metric.key as keyof Benchmark];
                  const medianValue = benchmark?.[metric.medianKey as keyof Benchmark];
                  const hasCompany = isValidMetricNumber(companyValue);
                  const hasMedian = isValidMetricNumber(medianValue);

                  return (
                    <TableCell key={year} className="text-center px-4">
                      {hasData ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="flex flex-col items-center">
                            <div className="font-bold text-[#2c2c2c] text-base bg-white px-3 py-1 rounded-lg shadow-sm border border-[#d9cba6]/20">
                              {hasCompany ? metric.format(companyValue) : "—"}
                            </div>
                            <div className="text-xs text-[#8b7355]/70 mt-1">Company</div>
                          </div>
                          <div className="w-px h-8 bg-[#d9cba6]/40" />
                          <div className="flex flex-col items-center">
                            <div className="font-bold text-[#c4965f] text-base bg-[#d9cba6]/10 px-3 py-1 rounded-lg border border-[#d9cba6]/30">
                              {hasMedian ? metric.format(medianValue) : "—"}
                            </div>
                            <div className="text-xs text-[#8b7355]/70 mt-1">Peers</div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-[#8b7355]/50">N/A</span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </ErrorBoundary>
  );
};

export default YearComparisonTable;
